import { useEffect, useState } from "react";
import api, { formatXOF } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { AdminAISettings } from "../components/AdminAISettings";
import { Loader2, Users, Coins, TrendingUp, Shield, Settings, Trash2, KeyRound, ShieldPlus, ShieldMinus, Wallet, Layers, CheckCircle2, Clock, Pencil, UserX, ListChecks, Plus, RefreshCw, AlertTriangle, LifeBuoy, Paperclip } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [cagnottes, setCagnottes] = useState([]);
  const [tontines, setTontines] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reset password dialog
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, password: "" });
  // Edit user dialog
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null, name: "", email: "", phone: "" });
  const [savingUser, setSavingUser] = useState(false);
  // Delete user confirm
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null });
  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState({ open: false, type: null, id: null, label: "" });
  // Config form
  const [cfgForm, setCfgForm] = useState({ tontine_commission_pct: "", cagnotte_fee_pct: "", transaction_fee_pct: "", min_contribution_xof: "", admin_payout_phone: "", admin_payout_provider: "wave", admin_payout_name: "", admin_payout_pct: "" });
  // Contributions dialog
  const [contribDialog, setContribDialog] = useState({ open: false, cagnotte: null, contributions: [], consistency: null, loading: false });
  const [newContrib, setNewContrib] = useState({ amount: "", contributor_name: "", contributor_phone: "", message: "", provider: "manual", status: "completed" });
  // Tontine batches
  const [batchWeeks, setBatchWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [batch, setBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  // Audit log
  const [audit, setAudit] = useState({ entries: [], total: 0, loading: false });
  const [auditFilter, setAuditFilter] = useState({ action: "", actor: "" });
  // Support tickets
  const [tickets, setTickets] = useState({ tickets: [], total: 0, counts: {}, loading: false });
  const [ticketFilter, setTicketFilter] = useState({ status: "", module: "", q: "" });
  const [ticketDialog, setTicketDialog] = useState({ open: false, ticket: null, newStatus: "", newNote: "", saving: false });

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, c, t, cfg] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/cagnottes"),
        api.get("/admin/tontines"),
        api.get("/admin/config"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setCagnottes(c.data);
      setTontines(t.data);
      setConfig(cfg.data);
      setCfgForm({
        tontine_commission_pct: String(cfg.data.tontine_commission_pct),
        cagnotte_fee_pct: String(cfg.data.cagnotte_fee_pct),
        transaction_fee_pct: String(cfg.data.transaction_fee_pct),
        min_contribution_xof: String(cfg.data.min_contribution_xof),
        admin_payout_phone: String(cfg.data.admin_payout_phone || ""),
        admin_payout_provider: String(cfg.data.admin_payout_provider || "wave"),
        admin_payout_name: String(cfg.data.admin_payout_name || ""),
        admin_payout_pct: String(cfg.data.admin_payout_pct ?? 4),
      });
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Load batch weeks + current audit log when their tabs are activated
  const loadBatchWeeks = async () => {
    try {
      const { data } = await api.get("/admin/tontine-batches/weeks");
      setBatchWeeks(data.weeks || []);
      if ((data.weeks || []).length && !selectedWeek) {
        setSelectedWeek(data.weeks[0].week);
      }
    } catch (e) {
      toast.error("Erreur chargement semaines");
    }
  };

  const loadBatch = async (week) => {
    if (!week) return;
    setBatchLoading(true);
    try {
      const { data } = await api.get(`/admin/tontine-batches/${week}`);
      setBatch(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement batch");
      setBatch(null);
    } finally {
      setBatchLoading(false);
    }
  };

  const downloadBatchPdf = async (week) => {
    try {
      const resp = await api.get(`/admin/tontine-batches/${week}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sen-natt-batch-${week}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur téléchargement PDF");
    }
  };

  const loadAudit = async () => {
    setAudit((a) => ({ ...a, loading: true }));
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (auditFilter.action) params.set("action", auditFilter.action);
      if (auditFilter.actor) params.set("actor", auditFilter.actor);
      const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
      setAudit({ entries: data.entries || [], total: data.total || 0, loading: false });
    } catch (e) {
      toast.error("Erreur chargement journal");
      setAudit({ entries: [], total: 0, loading: false });
    }
  };

  useEffect(() => {
    if (tab === "batches") loadBatchWeeks();
    if (tab === "audit") loadAudit();
    if (tab === "incidents") loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadTickets = async () => {
    setTickets((t) => ({ ...t, loading: true }));
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (ticketFilter.status) params.set("status", ticketFilter.status);
      if (ticketFilter.module) params.set("module", ticketFilter.module);
      if (ticketFilter.q) params.set("q", ticketFilter.q);
      const { data } = await api.get(`/admin/support-tickets?${params.toString()}`);
      setTickets({ tickets: data.tickets || [], total: data.total || 0, counts: data.counts || {}, loading: false });
    } catch (e) {
      toast.error("Erreur chargement incidents");
      setTickets({ tickets: [], total: 0, counts: {}, loading: false });
    }
  };

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/admin/support-tickets/${id}`);
      setTicketDialog({ open: true, ticket: data, newStatus: data.status, newNote: "", saving: false });
    } catch (e) {
      toast.error("Erreur chargement ticket");
    }
  };

  const saveTicket = async () => {
    if (!ticketDialog.ticket) return;
    const payload = {};
    if (ticketDialog.newStatus && ticketDialog.newStatus !== ticketDialog.ticket.status) payload.status = ticketDialog.newStatus;
    if (ticketDialog.newNote.trim()) payload.note = ticketDialog.newNote.trim();
    if (!payload.status && !payload.note) return toast.error("Ajoutez une note ou changez le statut");
    setTicketDialog((d) => ({ ...d, saving: true }));
    try {
      const { data } = await api.patch(`/admin/support-tickets/${ticketDialog.ticket.id}`, payload);
      toast.success("Ticket mis à jour");
      setTicketDialog({ open: true, ticket: data, newStatus: data.status, newNote: "", saving: false });
      loadTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setTicketDialog((d) => ({ ...d, saving: false }));
    }
  };

  const downloadAttachment = async (ticketId, filename) => {
    try {
      const resp = await api.get(`/admin/support-tickets/${ticketId}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "piece-jointe";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur téléchargement");
    }
  };

  useEffect(() => {
    if (tab === "batches" && selectedWeek) loadBatch(selectedWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const doResetPassword = async () => {
    if (!resetDialog.password || resetDialog.password.length < 6) {
      toast.error("Mot de passe : 6 caractères minimum");
      return;
    }
    try {
      await api.post(`/admin/users/${resetDialog.user.user_id}/reset-password`, { new_password: resetDialog.password });
      toast.success(`Mot de passe réinitialisé pour ${resetDialog.user.email}`);
      setResetDialog({ open: false, user: null, password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.post(`/admin/users/${u.user_id}/role?role=${newRole}`);
      toast.success(`Rôle modifié : ${newRole}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openEditUser = (u) => {
    setEditUserDialog({ open: true, user: u, name: u.name || "", email: u.email || "", phone: u.phone || "" });
  };

  const saveEditUser = async () => {
    setSavingUser(true);
    try {
      const payload = { name: editUserDialog.name, email: editUserDialog.email };
      if (editUserDialog.phone) payload.phone = editUserDialog.phone;
      await api.patch(`/admin/users/${editUserDialog.user.user_id}`, payload);
      toast.success("Utilisateur mis à jour");
      setEditUserDialog({ open: false, user: null, name: "", email: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSavingUser(false);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${deleteUserDialog.user.user_id}`);
      toast.success("Utilisateur supprimé");
      setDeleteUserDialog({ open: false, user: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const doDelete = async () => {
    const { type, id } = deleteTarget;
    try {
      if (type === "cagnotte") await api.delete(`/admin/cagnottes/${id}`);
      else if (type === "tontine") await api.delete(`/admin/tontines/${id}`);
      toast.success("Supprimé");
      setDeleteTarget({ open: false, type: null, id: null, label: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const markCagnottePaid = async (c) => {
    if (!window.confirm(`Confirmer que les fonds (${formatXOF(c.payout_amount || 0)}) ont bien été versés au créateur de « ${c.title} » ?\n\nLa cagnotte sera clôturée définitivement (plus aucune modification possible).`)) return;
    try {
      await api.post(`/admin/cagnottes/${c.id}/mark-paid`);
      toast.success("Cagnotte marquée comme versée et clôturée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const retryCommission = async (cagnotteId) => {
    if (!window.confirm("Renvoyer la commission plateforme (5%) au numéro Admin ?")) return;
    try {
      const { data } = await api.post(`/admin/cagnottes/${cagnotteId}/retry-commission`);
      toast.success(`Commission envoyée : ${formatXOF(data.amount_xof)} via ${data.provider === "orange" ? "Orange Money" : "Wave"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du renvoi de la commission");
    }
  };

  const openContribDialog = async (cagnotte) => {
    setContribDialog({ open: true, cagnotte, contributions: [], consistency: null, loading: true });
    setNewContrib({ amount: "", contributor_name: "", contributor_phone: "", message: "", provider: "manual", status: "completed" });
    try {
      const { data } = await api.get(`/admin/cagnottes/${cagnotte.id}/contributions`);
      setContribDialog({ open: true, cagnotte: data.cagnotte, contributions: data.contributions, consistency: data.consistency, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
      setContribDialog({ open: false, cagnotte: null, contributions: [], consistency: null, loading: false });
    }
  };

  const reloadContribDialog = async (cagnotteId) => {
    try {
      const { data } = await api.get(`/admin/cagnottes/${cagnotteId}/contributions`);
      setContribDialog((d) => ({ ...d, cagnotte: data.cagnotte, contributions: data.contributions, consistency: data.consistency }));
      load(); // refresh main list
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de rechargement");
    }
  };

  const markContribPaid = async (contribId) => {
    if (!window.confirm("Marquer ce paiement comme validé ? Il sera comptabilisé dans le total de la cagnotte.")) return;
    try {
      const { data } = await api.post(`/admin/contributions/${contribId}/mark-paid`);
      toast.success(`Paiement validé : +${formatXOF(data.amount)}${data.reconcile?.corrected ? " (total corrigé)" : ""}`);
      await reloadContribDialog(contribDialog.cagnotte.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteContrib = async (contribId) => {
    if (!window.confirm("Supprimer définitivement ce paiement ? Le total de la cagnotte sera recalculé.")) return;
    try {
      const { data } = await api.delete(`/admin/contributions/${contribId}`);
      toast.success(`Paiement supprimé${data.reconcile?.corrected ? " (total recalculé)" : ""}`);
      await reloadContribDialog(contribDialog.cagnotte.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const createContrib = async () => {
    const amount = Number(newContrib.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    if (!newContrib.contributor_name.trim()) return toast.error("Nom du contributeur requis");
    try {
      const payload = {
        cagnotte_id: contribDialog.cagnotte.id,
        amount,
        contributor_name: newContrib.contributor_name.trim(),
        contributor_phone: newContrib.contributor_phone.trim() || undefined,
        message: newContrib.message.trim() || undefined,
        provider: newContrib.provider,
        status: newContrib.status,
      };
      const { data } = await api.post(`/admin/contributions`, payload);
      toast.success(`Paiement ajouté : +${formatXOF(amount)}${data.reconcile?.corrected ? " (total mis à jour)" : ""}`);
      setNewContrib({ amount: "", contributor_name: "", contributor_phone: "", message: "", provider: "manual", status: "completed" });
      await reloadContribDialog(contribDialog.cagnotte.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const reconcileCagnotte = async (cagnotteId) => {
    try {
      const { data } = await api.post(`/admin/cagnottes/${cagnotteId}/reconcile`);
      if (data.corrected) {
        toast.success(`Total corrigé : ${formatXOF(data.previous)} → ${formatXOF(data.computed)}`);
      } else {
        toast.success("Total cohérent, aucune correction nécessaire");
      }
      if (contribDialog.open && contribDialog.cagnotte?.id === cagnotteId) {
        await reloadContribDialog(cagnotteId);
      } else {
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const sweepPendingContributions = async () => {
    if (!window.confirm("Interroger le proxy pour tous les paiements en attente et finaliser ceux qui sont résolus ?")) return;
    try {
      const { data } = await api.post("/admin/contributions/sweep-pending");
      const total = (data.completed || 0) + (data.cancelled || 0);
      if (total > 0) {
        toast.success(`Sweep terminé : ${data.completed} validé(s), ${data.cancelled} annulé(s) sur ${data.checked} vérifié(s)`);
      } else {
        toast.success(`Aucune transition — ${data.checked} paiement(s) en attente vérifié(s)`);
      }
      if (contribDialog.open && contribDialog.cagnotte?.id) {
        await reloadContribDialog(contribDialog.cagnotte.id);
      } else {
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sweep");
    }
  };

  const saveConfig = async () => {
    try {
      const payload = {
        tontine_commission_pct: parseFloat(cfgForm.tontine_commission_pct),
        cagnotte_fee_pct: parseFloat(cfgForm.cagnotte_fee_pct),
        transaction_fee_pct: parseFloat(cfgForm.transaction_fee_pct),
        min_contribution_xof: parseInt(cfgForm.min_contribution_xof, 10),
        admin_payout_phone: (cfgForm.admin_payout_phone || "").trim(),
        admin_payout_provider: cfgForm.admin_payout_provider,
        admin_payout_name: (cfgForm.admin_payout_name || "").trim() || "Sen Cagnotte Admin",
        admin_payout_pct: parseFloat(cfgForm.admin_payout_pct),
      };
      const { data } = await api.put("/admin/config", payload);
      setConfig(data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-7xl" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-yellow-500 flex items-center justify-center text-white">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-title">Administration</h1>
          <p className="text-sm text-muted-foreground">Vue globale et configuration de la plateforme</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="mb-6 -mx-4 md:mx-0 overflow-x-auto no-scrollbar">
          <TabsList className="inline-flex md:inline-grid md:grid-cols-8 md:w-full md:max-w-6xl gap-1 px-4 md:px-1 whitespace-nowrap">
            <TabsTrigger value="overview" data-testid="tab-overview" className="shrink-0">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="cagnottes" data-testid="tab-cagnottes" className="shrink-0">Cagnottes</TabsTrigger>
            <TabsTrigger value="tontines" data-testid="tab-tontines" className="shrink-0">Tontines</TabsTrigger>
            <TabsTrigger value="batches" data-testid="tab-batches" className="shrink-0">Batches</TabsTrigger>
            <TabsTrigger value="incidents" data-testid="tab-incidents" className="shrink-0">Incidents</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="shrink-0">Utilisateurs</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit" className="shrink-0">Journal</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config" className="shrink-0">Configuration</TabsTrigger>
          </TabsList>
        </div>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6">
          {stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard testid="stat-users" icon={Users} label="Utilisateurs" value={stats.users.total} sub={`${stats.users.admins} administrateur·s`} />
                <StatCard testid="stat-cagnottes" icon={Wallet} label="Cagnottes" value={stats.cagnottes.total} sub={`${stats.cagnottes.public} publiques`} />
                <StatCard testid="stat-tontines" icon={Layers} label="Tontines" value={stats.tontines.total} sub={`${stats.tontines.active} actives`} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard testid="stat-cagnotte-collected" icon={Coins} label="Collecté via cagnottes" value={formatXOF(stats.cagnottes.total_collected_xof)} sub={`${stats.cagnottes.contributions_count} contributions`} />
                <StatCard testid="stat-tontine-collected" icon={Coins} label="Collecté via tontines" value={formatXOF(stats.tontines.total_collected_xof)} sub={`${stats.tontines.payments_count} cotisations`} />
                <StatCard testid="stat-commission" icon={TrendingUp} label="Commission tontines" value={formatXOF(stats.tontines.total_commission_xof)} sub="Revenus plateforme" />
              </div>
            </>
          )}
        </TabsContent>

        {/* CAGNOTTES */}
        <TabsContent value="cagnottes">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>Toutes les cagnottes ({cagnottes.length})</CardTitle>
                  <CardDescription>Vue exhaustive de chaque cagnotte avec total collecté</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sweepPendingContributions}
                  data-testid="sweep-pending-btn"
                  title="Interroger le proxy pour tous les paiements en attente et finaliser ceux qui sont résolus"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Vérifier paiements en attente
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-cagnottes-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Titre</th>
                    <th className="py-2 pr-4">État</th>
                    <th className="py-2 pr-4">Objectif</th>
                    <th className="py-2 pr-4">Collecté</th>
                    <th className="py-2 pr-4">Reversement</th>
                    <th className="py-2 pr-4">Commission (5%)</th>
                    <th className="py-2 pr-4">Créée</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {cagnottes.map((c) => {
                    const processing = c.payout_status === "processing";
                    const failed = c.payout_status === "failed";
                    const settled = c.status === "settled" || c.payout_status === "completed";
                    return (
                      <tr key={c.id} className={`border-b last:border-0 ${processing || failed ? "bg-amber-50/40 dark:bg-amber-500/5" : ""}`}>
                        <td className="py-2 pr-4 font-medium max-w-xs truncate">{c.title}</td>
                        <td className="py-2 pr-4">
                          {settled ? <Badge className="bg-emerald-600">Versée</Badge> : c.status === "closed" ? <Badge variant="outline" className="border-amber-400 text-amber-700">Clôturée</Badge> : <Badge variant="secondary">Active</Badge>}
                        </td>
                        <td className="py-2 pr-4">{formatXOF(c.goal_amount || 0)}</td>
                        <td className="py-2 pr-4 font-semibold">
                          <div className="flex items-center gap-2">
                            <span>{formatXOF(c.collected_xof)}</span>
                            {Math.abs(c.total_drift_xof || 0) >= 0.01 && (
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300"
                                title={`Incohérence : total cagnotte = ${formatXOF(c.current_amount || 0)} vs somme paiements = ${formatXOF(c.collected_xof)}`}
                                data-testid={`drift-badge-${c.id}`}
                              >
                                ⚠ Δ {formatXOF(c.total_drift_xof)}
                              </span>
                            )}
                            {(c.pending_count || 0) > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" title={`${c.pending_count} paiement(s) en attente`}>
                                {c.pending_count} pending
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-4">
                          {processing && (
                            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-semibold" data-testid={`payout-processing-${c.id}`}>
                              <Clock className="h-3.5 w-3.5" /> {formatXOF(c.payout_amount || 0)} en cours
                            </span>
                          )}
                          {failed && (
                            <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400 font-semibold" title={c.payout_error || ""}>
                              Échec — voir logs
                            </span>
                          )}
                          {settled && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                              <CheckCircle2 className="h-3.5 w-3.5" /> {formatXOF(c.payout_amount || 0)} versé
                            </span>
                          )}
                          {!processing && !failed && !settled && <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2 pr-4">
                          {(c.payout_fee || 0) > 0 ? (
                            c.admin_commission_status === "completed" ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" data-testid={`admin-commission-ok-${c.id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> {formatXOF(c.payout_fee)} <span className="text-xs text-muted-foreground">({c.admin_commission_provider === "orange" ? "OM" : "Wave"})</span>
                              </span>
                            ) : c.admin_commission_status === "failed" ? (
                              <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400" title={c.admin_commission_error || ""} data-testid={`admin-commission-failed-${c.id}`}>
                                {formatXOF(c.payout_fee)} — échec
                              </span>
                            ) : c.admin_commission_status === "skipped" ? (
                              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400" title={c.admin_commission_error || ""}>
                                {formatXOF(c.payout_fee)} — non configuré
                              </span>
                            ) : c.payout_status === "completed" ? (
                              <span className="text-muted-foreground">{formatXOF(c.payout_fee)}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">{(c.created_at || "").slice(0, 10)}</td>
                        <td className="py-2 flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openContribDialog(c)} data-testid={`view-contribs-${c.id}`} title="Gérer les paiements">
                            <ListChecks className="h-4 w-4 text-primary" />
                          </Button>
                          {c.admin_commission_status && c.admin_commission_status !== "completed" && c.payout_status === "completed" && (c.payout_fee || 0) > 0 && (
                            <Button size="sm" variant="outline" onClick={() => retryCommission(c.id)} data-testid={`retry-commission-${c.id}`} className="mr-1">
                              Renvoyer 5%
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => setDeleteTarget({ open: true, type: "cagnotte", id: c.id, label: c.title })} data-testid={`delete-cagnotte-${c.id}`}>
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TONTINES */}
        <TabsContent value="tontines">
          <Card>
            <CardHeader>
              <CardTitle>Toutes les tontines ({tontines.length})</CardTitle>
              <CardDescription>Vue exhaustive avec totaux et commissions</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-tontines-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Nom</th>
                    <th className="py-2 pr-4">Statut</th>
                    <th className="py-2 pr-4">Cotisation</th>
                    <th className="py-2 pr-4">Particip.</th>
                    <th className="py-2 pr-4">Collecté</th>
                    <th className="py-2 pr-4">Commission</th>
                    <th className="py-2 pr-4">Paiements</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {tontines.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium max-w-xs truncate">{t.name}</td>
                      <td className="py-2 pr-4"><Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge></td>
                      <td className="py-2 pr-4">{formatXOF(t.contribution_amount)}</td>
                      <td className="py-2 pr-4">{t.participants_count}</td>
                      <td className="py-2 pr-4 font-semibold">{formatXOF(t.collected_xof)}</td>
                      <td className="py-2 pr-4 text-emerald-700">{formatXOF(t.commission_xof)}</td>
                      <td className="py-2 pr-4">{t.payments_count}</td>
                      <td className="py-2">
                        <Button size="icon" variant="ghost" onClick={() => setDeleteTarget({ open: true, type: "tontine", id: t.id, label: t.name })} data-testid={`delete-tontine-${t.id}`}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BATCHES (weekly tontine payouts) */}
        <TabsContent value="batches" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" /> Batches hebdomadaires Tontines</CardTitle>
              <CardDescription>Récapitulatif des cotisations tontine encaissées par semaine ISO, avec export PDF.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-sm">Semaine :</Label>
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="batch-week-select"
                >
                  {batchWeeks.length === 0 && <option value="">— aucune semaine —</option>}
                  {batchWeeks.map((w) => (
                    <option key={w.week} value={w.week}>{w.week} · {w.count} paiement(s)</option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={loadBatchWeeks} data-testid="batch-refresh">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser
                </Button>
                {selectedWeek && (
                  <Button size="sm" onClick={() => downloadBatchPdf(selectedWeek)} className="btn-gradient rounded-full ml-auto" data-testid="batch-download-pdf">
                    Télécharger PDF récap
                  </Button>
                )}
              </div>

              {batchLoading ? (
                <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
              ) : batch && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Total encaissé</div>
                      <div className="text-xl font-bold" data-testid="batch-total">{formatXOF(batch.totals.total_paid)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Commissions</div>
                      <div className="text-xl font-bold text-primary">{formatXOF(batch.totals.total_commission)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Pénalités</div>
                      <div className="text-xl font-bold">{formatXOF(batch.totals.total_penalty)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Net bénéficiaires</div>
                      <div className="text-xl font-bold text-emerald-600">{formatXOF(batch.totals.net_to_beneficiaries)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {batch.totals.count} cotisation(s) · {batch.totals.tontines_count} tontine(s) · du {batch.start.slice(0, 10)} au {batch.end.slice(0, 10)}
                  </div>

                  {batch.tontines.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">Aucune cotisation encaissée durant cette semaine.</div>
                  ) : (
                    <div className="space-y-4">
                      {batch.tontines.map((t) => (
                        <div key={t.tontine_id} className="rounded-lg border" data-testid={`batch-tontine-${t.tontine_id}`}>
                          <div className="px-4 py-3 border-b bg-muted/30 flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="font-semibold">{t.name}</div>
                              <div className="text-xs text-muted-foreground">Organisateur : {t.organizer_name || "—"} · {t.organizer_email || ""}</div>
                            </div>
                            <div className="text-right text-xs">
                              <div>Total : <strong>{formatXOF(t.total_paid)}</strong></div>
                              <div>Net bénéficiaire : <strong className="text-emerald-600">{formatXOF(t.net_to_beneficiary)}</strong></div>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-muted-foreground">
                                  <th className="py-2 px-3">Tour</th>
                                  <th className="py-2 px-3">Participant</th>
                                  <th className="py-2 px-3">Cotisation</th>
                                  <th className="py-2 px-3">Commission</th>
                                  <th className="py-2 px-3">Pénalité</th>
                                  <th className="py-2 px-3">Total</th>
                                  <th className="py-2 px-3">Payé le</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t.payments.map((p) => (
                                  <tr key={p.payment_id} className="border-t">
                                    <td className="py-2 px-3">#{p.round_number}</td>
                                    <td className="py-2 px-3">{p.participant_name}</td>
                                    <td className="py-2 px-3">{formatXOF(p.amount)}</td>
                                    <td className="py-2 px-3">{formatXOF(p.commission)}</td>
                                    <td className="py-2 px-3">{formatXOF(p.penalty)}</td>
                                    <td className="py-2 px-3 font-semibold">{formatXOF(p.total_paid)}</td>
                                    <td className="py-2 px-3 text-muted-foreground">{(p.paid_at || "").slice(0, 16).replace("T", " ")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INCIDENTS (support tickets) */}
        <TabsContent value="incidents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-primary" /> Incidents & Support</CardTitle>
              <CardDescription>Tickets remontés par les utilisateurs. Filtrer par statut, module ou mot-clé.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["open", "in_progress", "resolved", "closed"].map((s) => (
                  <div key={s} className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">{s === "open" ? "Ouverts" : s === "in_progress" ? "En cours" : s === "resolved" ? "Résolus" : "Clôturés"}</div>
                    <div className={`text-2xl font-bold ${s === "open" ? "text-rose-600" : s === "in_progress" ? "text-amber-600" : s === "resolved" ? "text-emerald-600" : ""}`} data-testid={`ticket-count-${s}`}>
                      {tickets.counts?.[s] || 0}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <Label className="text-xs">Statut</Label>
                  <select value={ticketFilter.status} onChange={(e) => setTicketFilter((f) => ({ ...f, status: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ticket-filter-status">
                    <option value="">— tous —</option>
                    <option value="open">Ouverts</option>
                    <option value="in_progress">En cours</option>
                    <option value="resolved">Résolus</option>
                    <option value="closed">Clôturés</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Module</Label>
                  <select value={ticketFilter.module} onChange={(e) => setTicketFilter((f) => ({ ...f, module: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ticket-filter-module">
                    <option value="">— tous —</option>
                    <option value="cagnottes">Cagnottes</option>
                    <option value="tontines">Tontines</option>
                    <option value="paiement">Paiement</option>
                    <option value="compte">Compte</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Recherche</Label>
                  <Input value={ticketFilter.q} onChange={(e) => setTicketFilter((f) => ({ ...f, q: e.target.value }))} placeholder="sujet, email, n° ticket…" className="mt-1" data-testid="ticket-filter-q" />
                </div>
                <Button onClick={loadTickets} variant="outline" data-testid="ticket-refresh">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Filtrer / actualiser
                </Button>
              </div>

              {tickets.loading ? (
                <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
              ) : tickets.tickets.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Aucun ticket ne correspond aux filtres.</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm" data-testid="tickets-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="py-2 px-3">N°</th>
                        <th className="py-2 px-3">Créé</th>
                        <th className="py-2 px-3">Statut</th>
                        <th className="py-2 px-3">Module</th>
                        <th className="py-2 px-3">Sujet</th>
                        <th className="py-2 px-3">Contact</th>
                        <th className="py-2 px-3">PJ</th>
                        <th className="py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.tickets.map((t) => (
                        <tr key={t.id} className="border-t hover:bg-muted/30" data-testid={`ticket-row-${t.id}`}>
                          <td className="py-2 px-3 font-mono text-xs">{t.ticket_number}</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="py-2 px-3">
                            {t.status === "open" ? (
                              <Badge className="bg-rose-600">Ouvert</Badge>
                            ) : t.status === "in_progress" ? (
                              <Badge className="bg-amber-500">En cours</Badge>
                            ) : t.status === "resolved" ? (
                              <Badge className="bg-emerald-600">Résolu</Badge>
                            ) : (
                              <Badge variant="outline">Clôturé</Badge>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">{t.module}</td>
                          <td className="py-2 px-3 max-w-xs truncate" title={t.subject}>{t.subject}</td>
                          <td className="py-2 px-3 text-xs">
                            <div>{t.reporter_name}</div>
                            <div className="text-muted-foreground">{t.reporter_email}</div>
                          </td>
                          <td className="py-2 px-3">{t.attachment ? <Paperclip className="h-4 w-4 text-primary" /> : "—"}</td>
                          <td className="py-2 px-3">
                            <Button size="sm" variant="outline" onClick={() => openTicket(t.id)} data-testid={`open-ticket-${t.id}`}>Voir</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Tous les utilisateurs ({users.length})</CardTitle>
              <CardDescription>Gestion des rôles et des mots de passe</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">Nom</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Rôle</th>
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2 pr-4">Créé</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{u.name}</td>
                      <td className="py-2 pr-4">{u.email}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role || "user"}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs">{u.provider}</td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="py-2 flex gap-1">
                        <Button size="icon" variant="ghost" title="Modifier" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.user_id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Réinitialiser mot de passe" onClick={() => setResetDialog({ open: true, user: u, password: "" })} disabled={u.provider !== "local"} data-testid={`reset-pwd-${u.user_id}`}>
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title={u.role === "admin" ? "Retirer admin" : "Promouvoir admin"} onClick={() => toggleRole(u)} data-testid={`role-${u.user_id}`}>
                          {u.role === "admin" ? <ShieldMinus className="h-4 w-4 text-rose-600" /> : <ShieldPlus className="h-4 w-4 text-emerald-600" />}
                        </Button>
                        <Button size="icon" variant="ghost" title="Supprimer" onClick={() => setDeleteUserDialog({ open: true, user: u })} data-testid={`delete-user-${u.user_id}`}>
                          <UserX className="h-4 w-4 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUDIT LOG */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5" /> Journal admin</CardTitle>
              <CardDescription>Toutes les actions administrateur : validations, suppressions, changements de config.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-xs">Type d'action</Label>
                  <select value={auditFilter.action} onChange={(e) => setAuditFilter((f) => ({ ...f, action: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="audit-filter-action">
                    <option value="">— toutes les actions —</option>
                    <option value="config">Configuration</option>
                    <option value="user">Utilisateurs</option>
                    <option value="cagnotte">Cagnottes</option>
                    <option value="contribution">Contributions</option>
                    <option value="tontine">Tontines</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Admin (email)</Label>
                  <Input value={auditFilter.actor} onChange={(e) => setAuditFilter((f) => ({ ...f, actor: e.target.value }))} placeholder="admin@…" className="mt-1" data-testid="audit-filter-actor" />
                </div>
                <Button onClick={loadAudit} variant="outline" data-testid="audit-refresh">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Filtrer / actualiser
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">{audit.total} entrée(s)</div>
              {audit.loading ? (
                <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
              ) : audit.entries.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Aucune action enregistrée pour ces filtres.</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-xs" data-testid="audit-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="py-2 px-3">Date (UTC)</th>
                        <th className="py-2 px-3">Admin</th>
                        <th className="py-2 px-3">Action</th>
                        <th className="py-2 px-3">Cible</th>
                        <th className="py-2 px-3">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.entries.map((e) => (
                        <tr key={e.id} className="border-t align-top" data-testid={`audit-row-${e.id}`}>
                          <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                          <td className="py-2 px-3">
                            <div className="font-medium">{e.admin_name || e.admin_email}</div>
                            <div className="text-muted-foreground">{e.admin_email}</div>
                          </td>
                          <td className="py-2 px-3"><Badge variant="secondary" className="font-mono text-[10px]">{e.action}</Badge></td>
                          <td className="py-2 px-3">
                            {e.target_type ? (
                              <div>
                                <div className="text-muted-foreground">{e.target_type}</div>
                                <div className="font-mono text-[10px]">{(e.target_id || "").slice(0, 16)}</div>
                              </div>
                            ) : "—"}
                          </td>
                          <td className="py-2 px-3">
                            <pre className="whitespace-pre-wrap break-words text-[10px] text-muted-foreground max-w-xs">{JSON.stringify(e.metadata || {}, null, 0)}</pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFIG */}
        <TabsContent value="config">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Configuration de la plateforme</CardTitle>
              <CardDescription>Marges, commissions et limites appliquées à toutes les nouvelles transactions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Commission tontines (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={cfgForm.tontine_commission_pct} onChange={(e) => setCfgForm((f) => ({ ...f, tontine_commission_pct: e.target.value }))} data-testid="cfg-tontine-pct" />
                <p className="text-xs text-muted-foreground">Pourcentage prélevé par Sen Cagnotte sur chaque cotisation tontine reçue. Actuel : <strong>{config?.tontine_commission_pct}%</strong></p>
              </div>
              <div className="space-y-2">
                <Label>Commission cagnottes à la remise des fonds (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={cfgForm.cagnotte_fee_pct} onChange={(e) => setCfgForm((f) => ({ ...f, cagnotte_fee_pct: e.target.value }))} data-testid="cfg-cagnotte-fee" />
                <p className="text-xs text-muted-foreground">Pourcentage prélevé au moment du payout vers le créateur de la cagnotte. Actuel : <strong>{config?.cagnotte_fee_pct}%</strong></p>
              </div>
              <div className="space-y-2">
                <Label>Frais de transaction (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={cfgForm.transaction_fee_pct} onChange={(e) => setCfgForm((f) => ({ ...f, transaction_fee_pct: e.target.value }))} data-testid="cfg-tx-fee" />
                <p className="text-xs text-muted-foreground">Ajouté au montant payé par le contributeur (couvre les frais Wave / Orange Money). Actuel : <strong>{config?.transaction_fee_pct}%</strong></p>
              </div>
              <div className="space-y-2">
                <Label>Contribution minimum (FCFA)</Label>
                <Input type="number" min="0" value={cfgForm.min_contribution_xof} onChange={(e) => setCfgForm((f) => ({ ...f, min_contribution_xof: e.target.value }))} data-testid="cfg-min-contrib" />
                <p className="text-xs text-muted-foreground">Montant plancher accepté. Actuel : <strong>{formatXOF(config?.min_contribution_xof || 0)}</strong></p>
              </div>

              <div className="pt-4 mt-2 border-t space-y-4">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Reversement automatique de la commission (5%)</div>
                  <p className="text-xs text-muted-foreground mt-1">À chaque payout d'une cagnotte, la commission plateforme est envoyée automatiquement à ce numéro.</p>
                </div>
                <div className="space-y-2">
                  <Label>Numéro Admin (Wave ou Orange Money)</Label>
                  <Input type="tel" placeholder="+221778312002" value={cfgForm.admin_payout_phone} onChange={(e) => setCfgForm((f) => ({ ...f, admin_payout_phone: e.target.value }))} data-testid="cfg-admin-phone" />
                  <p className="text-xs text-muted-foreground">Formats acceptés : <code>+221XXXXXXXXX</code>, <code>221XXXXXXXXX</code>, <code>77XXXXXXX</code>. Actuel : <strong>{config?.admin_payout_phone || "non configuré"}</strong></p>
                </div>
                <div className="space-y-2">
                  <Label>Opérateur pour la commission</Label>
                  <div className="flex gap-3">
                    <label className={`flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${cfgForm.admin_payout_provider === "wave" ? "border-primary bg-primary/5" : ""}`}>
                      <input type="radio" name="admin_provider" value="wave" checked={cfgForm.admin_payout_provider === "wave"} onChange={(e) => setCfgForm((f) => ({ ...f, admin_payout_provider: e.target.value }))} data-testid="cfg-admin-provider-wave" />
                      <span>Wave</span>
                    </label>
                    <label className={`flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${cfgForm.admin_payout_provider === "orange" ? "border-primary bg-primary/5" : ""}`}>
                      <input type="radio" name="admin_provider" value="orange" checked={cfgForm.admin_payout_provider === "orange"} onChange={(e) => setCfgForm((f) => ({ ...f, admin_payout_provider: e.target.value }))} data-testid="cfg-admin-provider-orange" />
                      <span>Orange Money</span>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">Actuel : <strong>{config?.admin_payout_provider === "orange" ? "Orange Money" : "Wave"}</strong></p>
                </div>
                <div className="space-y-2">
                  <Label>Nom bénéficiaire (affiché sur Wave)</Label>
                  <Input type="text" placeholder="Sen Cagnotte Admin" value={cfgForm.admin_payout_name} onChange={(e) => setCfgForm((f) => ({ ...f, admin_payout_name: e.target.value }))} data-testid="cfg-admin-name" />
                </div>
                <div className="space-y-2">
                  <Label>% effectivement reversé à l'Admin</Label>
                  <Input type="number" step="0.1" min="0" max="100" value={cfgForm.admin_payout_pct} onChange={(e) => setCfgForm((f) => ({ ...f, admin_payout_pct: e.target.value }))} data-testid="cfg-admin-payout-pct" />
                  <p className="text-xs text-muted-foreground">
                    Doit être ≤ Frais plateforme ({config?.cagnotte_fee_pct || 5}%). La différence couvre les frais de transfert Wave/OM. <strong>Défaut : 4%</strong> → propriétaire 95% + Admin 4% + 1% réserve frais = 100%. Actuel : <strong>{config?.admin_payout_pct ?? 4}%</strong>.
                  </p>
                </div>
              </div>

              <Button onClick={saveConfig} className="btn-gradient rounded-full" data-testid="save-config-btn">
                Enregistrer la configuration
              </Button>
            </CardContent>
          </Card>
          <AdminAISettings />
        </TabsContent>
      </Tabs>

      {/* Ticket detail dialog */}
      <Dialog open={ticketDialog.open} onOpenChange={(open) => setTicketDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ticket-dialog">
          {ticketDialog.ticket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <LifeBuoy className="h-5 w-5 text-primary" />
                  Ticket {ticketDialog.ticket.ticket_number}
                </DialogTitle>
                <DialogDescription>{ticketDialog.ticket.subject}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Module</div><div className="font-medium">{ticketDialog.ticket.module}</div></div>
                  <div><div className="text-xs text-muted-foreground">Créé le</div><div>{(ticketDialog.ticket.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                  <div><div className="text-xs text-muted-foreground">Statut actuel</div><div className="font-medium">{ticketDialog.ticket.status}</div></div>
                  <div><div className="text-xs text-muted-foreground">Utilisateur lié</div><div className="text-xs font-mono">{ticketDialog.ticket.reporter_user_id ? ticketDialog.ticket.reporter_user_id.slice(0, 12) : "—"}</div></div>
                </div>

                <div className="rounded-lg border p-3">
                  <div className="text-xs font-semibold mb-2">Contact</div>
                  <div className="text-sm">{ticketDialog.ticket.reporter_name}</div>
                  <div className="text-xs text-muted-foreground">{ticketDialog.ticket.reporter_email}</div>
                  {ticketDialog.ticket.reporter_phone && <div className="text-xs text-muted-foreground">{ticketDialog.ticket.reporter_phone}</div>}
                </div>

                <div className="rounded-lg border p-3">
                  <div className="text-xs font-semibold mb-2">Message</div>
                  <pre className="text-sm whitespace-pre-wrap font-sans">{ticketDialog.ticket.message}</pre>
                </div>

                {(ticketDialog.ticket.error_code || ticketDialog.ticket.error_message || ticketDialog.ticket.target_url) && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-500/10 p-3">
                    <div className="text-xs font-semibold text-rose-800 dark:text-rose-300 mb-2">Contexte technique</div>
                    {ticketDialog.ticket.error_code && <div className="text-xs font-mono">Code : {ticketDialog.ticket.error_code}</div>}
                    {ticketDialog.ticket.error_message && <div className="text-xs">Erreur : {ticketDialog.ticket.error_message}</div>}
                    {ticketDialog.ticket.target_url && <div className="text-xs break-all">URL : <a href={ticketDialog.ticket.target_url} target="_blank" rel="noreferrer" className="underline">{ticketDialog.ticket.target_url}</a></div>}
                    {ticketDialog.ticket.target_id && <div className="text-xs font-mono">Cible : {ticketDialog.ticket.target_id}</div>}
                  </div>
                )}

                {ticketDialog.ticket.attachment && (
                  <div className="rounded-lg border p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-4 w-4 text-primary shrink-0" />
                      <div className="truncate text-sm">
                        {ticketDialog.ticket.attachment.filename}
                        <span className="text-muted-foreground text-xs ml-2">({Math.round((ticketDialog.ticket.attachment.size || 0) / 1024)} Ko · {ticketDialog.ticket.attachment.content_type})</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadAttachment(ticketDialog.ticket.id, ticketDialog.ticket.attachment.filename)} data-testid="ticket-download-attachment">
                      Télécharger
                    </Button>
                  </div>
                )}

                {ticketDialog.ticket.admin_notes?.length > 0 && (
                  <div className="rounded-lg border p-3">
                    <div className="text-xs font-semibold mb-2">Historique admin</div>
                    <div className="space-y-2">
                      {ticketDialog.ticket.admin_notes.map((n, i) => (
                        <div key={i} className="text-xs border-l-2 border-primary/40 pl-3">
                          <div className="text-muted-foreground">
                            {(n.timestamp || "").slice(0, 16).replace("T", " ")} · <strong>{n.admin_email}</strong>
                            {n.status_change && <span className="ml-2">→ statut {n.status_change.from} → <strong>{n.status_change.to}</strong></span>}
                          </div>
                          {n.text && <div className="mt-1 whitespace-pre-wrap">{n.text}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border p-3 space-y-3 bg-muted/20">
                  <div className="text-xs font-semibold">Traiter ce ticket</div>
                  <div>
                    <Label className="text-xs">Nouveau statut</Label>
                    <select value={ticketDialog.newStatus} onChange={(e) => setTicketDialog((d) => ({ ...d, newStatus: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ticket-new-status">
                      <option value="open">Ouvert</option>
                      <option value="in_progress">En cours</option>
                      <option value="resolved">Résolu</option>
                      <option value="closed">Clôturé</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Note interne (optionnel)</Label>
                    <Textarea rows={3} value={ticketDialog.newNote} onChange={(e) => setTicketDialog((d) => ({ ...d, newNote: e.target.value }))} placeholder="Action prise, retour à l'utilisateur, etc." className="mt-1" data-testid="ticket-new-note" />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setTicketDialog({ open: false, ticket: null, newStatus: "", newNote: "", saving: false })} className="rounded-full">Fermer</Button>
                <Button onClick={saveTicket} disabled={ticketDialog.saving} className="btn-gradient rounded-full" data-testid="ticket-save">
                  {ticketDialog.saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enregistrement…</> : "Enregistrer"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contributions management dialog */}
      <Dialog open={contribDialog.open} onOpenChange={(open) => setContribDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="admin-contribs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-primary" /> Paiements — {contribDialog.cagnotte?.title}</DialogTitle>
            <DialogDescription>Gérer les contributions de cette cagnotte (validation, ajout, suppression).</DialogDescription>
          </DialogHeader>
          {contribDialog.loading ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
          ) : (
            <div className="space-y-4">
              {/* Consistency banner */}
              {contribDialog.consistency && (
                contribDialog.consistency.coherent ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Total cohérent — <strong>{formatXOF(contribDialog.consistency.current_amount)}</strong> = somme des paiements validés.</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => reconcileCagnotte(contribDialog.cagnotte.id)} data-testid="reconcile-btn">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalculer
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-500/10 px-4 py-3 text-sm">
                    <div className="flex items-start gap-2 text-rose-800 dark:text-rose-300" data-testid="drift-warning">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <div><strong>Incohérence détectée</strong></div>
                        <div className="text-xs">Total cagnotte : <strong>{formatXOF(contribDialog.consistency.current_amount)}</strong> · Somme paiements validés : <strong>{formatXOF(contribDialog.consistency.completed_sum)}</strong> · Écart : <strong>{formatXOF(contribDialog.consistency.drift)}</strong></div>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => reconcileCagnotte(contribDialog.cagnotte.id)} className="btn-gradient rounded-full shrink-0" data-testid="reconcile-btn-warning">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Corriger maintenant
                    </Button>
                  </div>
                )
              )}

              {/* Contributions list */}
              <div className="rounded-lg border overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-contribs-table">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3">Contributeur</th>
                      <th className="py-2 px-3">Montant</th>
                      <th className="py-2 px-3">Provider</th>
                      <th className="py-2 px-3">Statut</th>
                      <th className="py-2 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contribDialog.contributions.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Aucun paiement pour cette cagnotte.</td></tr>
                    )}
                    {contribDialog.contributions.map((c) => (
                      <tr key={c.id} className="border-t" data-testid={`contrib-row-${c.id}`}>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{c.is_anonymous ? "Anonyme" : (c.contributor_name || "—")}</div>
                          {c.contributor_phone && <div className="text-xs text-muted-foreground">{c.contributor_phone}</div>}
                        </td>
                        <td className="py-2 px-3 font-semibold">{formatXOF(c.amount)}</td>
                        <td className="py-2 px-3 text-xs">
                          {c.provider === "wave" ? "Wave" : c.provider === "orange" ? "Orange Money" : c.provider === "manual" ? "Manuel" : c.provider}
                        </td>
                        <td className="py-2 px-3">
                          {c.status === "completed" ? (
                            <Badge className="bg-emerald-600" data-testid={`status-completed-${c.id}`}>Validé</Badge>
                          ) : c.status === "pending" ? (
                            <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid={`status-pending-${c.id}`}>En attente</Badge>
                          ) : (
                            <Badge variant="outline" className="border-rose-400 text-rose-700" data-testid={`status-failed-${c.id}`}>Échec</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex justify-end gap-1">
                            {c.status !== "completed" && (
                              <Button size="sm" variant="outline" onClick={() => markContribPaid(c.id)} data-testid={`mark-paid-${c.id}`} className="h-7 text-xs">
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Valider
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" onClick={() => deleteContrib(c.id)} data-testid={`delete-contrib-${c.id}`} className="h-7 w-7">
                              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add contribution */}
              <div className="rounded-lg border p-4 bg-muted/20">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Enregistrer un paiement manuel</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Montant (FCFA) *</Label>
                    <Input type="number" min="1" value={newContrib.amount} onChange={(e) => setNewContrib((n) => ({ ...n, amount: e.target.value }))} data-testid="new-contrib-amount" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Nom contributeur *</Label>
                    <Input value={newContrib.contributor_name} onChange={(e) => setNewContrib((n) => ({ ...n, contributor_name: e.target.value }))} data-testid="new-contrib-name" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone (optionnel)</Label>
                    <Input value={newContrib.contributor_phone} onChange={(e) => setNewContrib((n) => ({ ...n, contributor_phone: e.target.value }))} placeholder="+221771787379" className="mt-1" data-testid="new-contrib-phone" />
                  </div>
                  <div>
                    <Label className="text-xs">Provider</Label>
                    <select value={newContrib.provider} onChange={(e) => setNewContrib((n) => ({ ...n, provider: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="new-contrib-provider">
                      <option value="manual">Manuel (espèces / hors plateforme)</option>
                      <option value="wave">Wave</option>
                      <option value="orange">Orange Money</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Message (optionnel)</Label>
                    <Input value={newContrib.message} onChange={(e) => setNewContrib((n) => ({ ...n, message: e.target.value }))} className="mt-1" data-testid="new-contrib-message" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Statut</Label>
                    <div className="flex gap-3 mt-1">
                      {["completed", "pending", "failed"].map((s) => (
                        <label key={s} className={`flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${newContrib.status === s ? "border-primary bg-primary/5" : ""}`}>
                          <input type="radio" name="new_status" value={s} checked={newContrib.status === s} onChange={(e) => setNewContrib((n) => ({ ...n, status: e.target.value }))} />
                          <span>{s === "completed" ? "Validé" : s === "pending" ? "En attente" : "Échec"}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <Button onClick={createContrib} className="btn-gradient rounded-full mt-4" data-testid="submit-new-contrib">
                  <Plus className="h-4 w-4 mr-1" /> Enregistrer le paiement
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContribDialog({ open: false, cagnotte: null, contributions: [], consistency: null, loading: false })} className="rounded-full">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => setResetDialog((d) => ({ ...d, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>{resetDialog.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <Input type="text" value={resetDialog.password} onChange={(e) => setResetDialog((d) => ({ ...d, password: e.target.value }))} data-testid="reset-pwd-input" placeholder="Min 6 caractères" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog({ open: false, user: null, password: "" })} className="rounded-full">Annuler</Button>
            <Button onClick={doResetPassword} className="rounded-full btn-gradient" data-testid="reset-pwd-confirm">Réinitialiser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editUserDialog.open} onOpenChange={(open) => setEditUserDialog((d) => ({ ...d, open }))}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>{editUserDialog.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom complet</Label>
              <Input value={editUserDialog.name} onChange={(e) => setEditUserDialog((d) => ({ ...d, name: e.target.value }))} className="mt-1.5" data-testid="edit-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editUserDialog.email} onChange={(e) => setEditUserDialog((d) => ({ ...d, email: e.target.value }))} className="mt-1.5" data-testid="edit-user-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={editUserDialog.phone} onChange={(e) => setEditUserDialog((d) => ({ ...d, phone: e.target.value }))} placeholder="+221771787379" inputMode="tel" className="mt-1.5" data-testid="edit-user-phone" />
              <p className="text-xs text-muted-foreground mt-1">Formats : 77XXXXXXX, +221XXXXXXXXX, 00221XXXXXXXXX</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog({ open: false, user: null, name: "", email: "", phone: "" })} className="rounded-full">Annuler</Button>
            <Button onClick={saveEditUser} disabled={savingUser} className="rounded-full btn-gradient" data-testid="edit-user-save">
              {savingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <AlertDialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog((d) => ({ ...d, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprime définitivement <strong>{deleteUserDialog.user?.email}</strong>. Les cagnottes et tontines créées par cet utilisateur ne seront pas supprimées automatiquement mais deviendront orphelines. Action irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteTarget.open} onOpenChange={(open) => setDeleteTarget((d) => ({ ...d, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action supprime définitivement « {deleteTarget.label} » et toutes ses données associées (paiements, contributions, messages). Irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
